/*
  By choosing a date from the DatePicker, the time 00:00 is set by default. 
  This leads to problems with the min and max dates, since they are calculated at the exact moment 
  in which the step is accessed. So it could happen that by selecting the min date from the DatePicker (setting 
  the time value to 00:00), the selected value was smaller than the min date, causing problems in displaying the choice; 
  vice versa with the max date. Therefore it was necessary to set a min time (00:00:00) and a max time(23:59:59) by default.
*/

import React, { useEffect } from 'react'
import { first, isUndefined } from 'lodash'
import { DatePicker as DatePickerComponent } from '@ublique/components/DatePicker'
import useDateTimeFormat from 'utils/hooks/useDateTimeFormat'
import { I18N_DEFAULT } from 'config'
import moment from 'moment'
import {
  datesMap,
  formatDay,
  getDateRuleString,
  getDayByDeltaValue,
  getDefaultDate,
  timesMax,
  timesMin
} from '../helpers/date'

const DatePicker = ({
  t,
  id,
  label,
  value,
  min,
  max,
  deltaMin,
  deltaMax,
  invalid,
  disabled,
  hidden,
  setValue,
  invalidText,
  formValues,
  equals,
  defaultValue,
  deltaDefault,
  currentLanguage = I18N_DEFAULT,
  onUpdatePayloadMapper,
  isScheduledMode
}) => {
  const equality = equals ? formValues[equals.slice(1)] : null
  const { dateFormat } = useDateTimeFormat()

  const saveRuleString = (id, value, isScheduledMode) => {
    const event = new Date(value)
    const year = event.getFullYear()
    const month = `${event.getMonth() + 1}`.padStart(2, '0')
    const day = `${event.getDate()}`.padStart(2, '0')
    const dateResult = [year, month, day].join('-')

    const ruleString = getDateRuleString({
      value: dateResult,
      defaultValue,
      deltaDefault,
      dateFormat: 'YYYY-MM-DD',
      isScheduledMode
    })
    onUpdatePayloadMapper('date', id, ruleString)
  }

  useEffect(() => {
    if (isUndefined(value)) {
      setValue(id, '')
      const _value = getDefaultDate(value, deltaDefault, defaultValue)

      isScheduledMode && saveRuleString(id, _value, isScheduledMode)
    }
  }, [])

  useEffect(() => {
    if (equals) {
      setValue(id, equality)
    }
  }, [equals, equality])

  const onChange = (eventOrDates) => {
    const value = eventOrDates.target
      ? eventOrDates.target.value
      : eventOrDates[0]

    const seconds = moment().seconds()
    const minutes = moment().minutes()
    const hours = moment().hours()

    const newValue = isUndefined(value)
      ? ''
      : moment(value)
          .add(hours, 'hours')
          .add(minutes, 'minutes')
          .add(seconds, 'seconds')
          .toDate()
    setValue(id, newValue)
    isScheduledMode && saveRuleString(id, newValue, isScheduledMode)
  }

  const getMinMaxDate = (type, deltaType, timesType) => {
    if (!type) return undefined

    if (!deltaType) {
      return !datesMap[type] ? undefined : formatDay(type, timesType)
    } else {
      return getDayByDeltaValue(type, deltaType, timesType)
    }
  }

  if (hidden) {
    return null
  }

  return (
    <DatePickerComponent
      dateFormat={dateFormat.altValue}
      onChange={onChange}
      minDate={min && getMinMaxDate(min, deltaMin, timesMin)}
      maxDate={max && getMinMaxDate(max, deltaMax, timesMax)}
      datePickerType='single'
      value={new Date(value)}
      locale={first(currentLanguage.split('-'))}
    >
      <DatePickerComponent.Input
        id={id}
        labelText={t(label)}
        invalid={invalid}
        disabled={disabled}
        placeholder='yyyy/mm/dd'
        invalidText={invalidText}
        pattern={dateFormat.pattern || null}
      />
    </DatePickerComponent>
  )
}

export default DatePicker
